import { chunk } from "lodash";
import React from 'react';
import styled, { css } from "styled-components";

import autobind from "autobind-decorator";
import { Intent } from "core/components/IntentContext";
import { IViewProps, View } from 'core/decorators/View';
import ILocalCacheService from "core/service/cache/ILocalCacheService";
import { isEmpty } from "core/util/ArrayUtils";
import ServiceRegistry from "core/util/ServiceRegistry";
import StringUtil from "core/util/StringUtil";
import { iDef } from "core/util/TypeUtils";
import RowsConfig from './RowsConfig';

interface IRowItemProps {
	config: RowsConfig;
}

const FlexWrapper = styled.div<IRowItemProps>`
	align-content: flex-start;
	align-items: flex-start;
	display: flex;
	flex-wrap: wrap;
	flex-direction: row;
	justify-content: flex-start;
	margin-left: ${({config}: IRowItemProps) => iDef(config.padding) ? '-' + config.padding : undefined};
	margin-right: ${({config}: IRowItemProps) => iDef(config.padding) ? '-' + config.padding : undefined};
	margin-top: ${({config}: IRowItemProps) => iDef(config.padding) ? '-' + config.padding : undefined};
	margin-bottom: ${({config}: IRowItemProps) => iDef(config.padding) ? '-' + config.padding : undefined};
`;

const RowItem = styled.div<IRowItemProps>(({config}: IRowItemProps) => css`
	width: ${(1 / config.columns || 1) * 100}%;
	box-sizing: border-box;
	padding-left: ${config.padding};
	padding-right: ${config.padding};
	padding-top: ${config.padding};
	padding-bottom: ${config.padding};

	${config.breakpoints && config.breakpoints.map((bp) => `
		@media(max-width: ${bp.width}) {
			width: ${(1 / bp.columns || 1) * 100}%;
		}
	`)}
`);

export interface IRowsViewProps<I extends any | React.ReactNode> extends IViewProps<RowsConfig> {
	items?: I[]; // uses children if not defined
	getKey?: (item: I) => string;
	skeleton?: boolean; // skeleton enabled. default = true
	renderItem?: (item: I, index?: number, skeleton?: boolean) => React.ReactNode;
	renderItemSkeleton?: (index: number) => React.ReactNode;
}

/**
 * @overview container view that wraps each react node child in a div and allows responsive column styling
 * @author chills
 */
@View({id: "rows-container", configClass: RowsConfig})
export class RowsView<I> extends React.PureComponent<IRowsViewProps<I>> {

	@autobind
	private getStorageKey() {
		const { panelInstance, viewKey } = this.props;
		return StringUtil.format("view:rows:count:%s:%s", panelInstance, viewKey);
	}

	private renderItem = (item: I, index: number, skeleton: boolean) => {
		const { renderItem, renderItemSkeleton, config, getKey } = this.props;
		const key: string = (getKey && getKey(item)) || "slide-" + index;

		return (
			<RowItem key={key} config={config}>
				{(skeleton && ((renderItemSkeleton && renderItemSkeleton(index)) || (renderItem && renderItem(null, index, false)))) || (renderItem && renderItem(item)) || item}
			</RowItem>
		);
	}

	public render() {
		const { config, skeleton = true, renderItem, renderItemSkeleton } = this.props;

		const children = React.Children.toArray(this.props.children);
		const items = this.props.items || (children.length > 0 && children) || null;
		const cGroups: any[] = (config.itemsPerColumn > 1 && items && items.length > 1) ? chunk(items, config.itemsPerColumn) : items;

		let savedRowCount = 0;

		// TODO move this to proper lifecycle functions
		if(skeleton) {
			const storageService = ServiceRegistry.get(ILocalCacheService);
			const key = this.getStorageKey();

			// check if we are reading or saving the row count
			if(Array.isArray(items)) {

				// we have some data, update the row count guess
				storageService.putNumber(key, items.length);

			} else {

				// try to read saved row count
				savedRowCount = storageService.getNumber(key, 0);
			}
		}

		return (
			<FlexWrapper config={config}>
				{Array.isArray(cGroups) ? (
					cGroups.map((v, i) => this.renderItem(v, i, false))
				) : (
					Array.from({length: savedRowCount || config.defaultSkeletonCount}, (v, i) => this.renderItem(null, i, true))
				)}
			</FlexWrapper>
		);
	}
}



// WEBPACK FOOTER //
// ./src/common/views/rows/Rows.view.tsx